import React, { useCallback, useEffect } from 'react';
import DoubleArrowUp from '../../../assets/icons/double-arrow-up.svg';
import DoubleArrowDown from '../../../assets/icons/double-arrow-down.svg';
import ArrowUp from '../../../assets/icons/arrow-up.svg';
import ArrowDown from '../../../assets/icons/arrow-down.svg';
import { useStyles } from './Arrows.styles';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { useLayoutContext } from '../../../Context/Layout.context';

let arrowKeyPressHandler: ((event: KeyboardEvent) => void) | null = null;

const removeArrowKeyPressHandler = () => {
  if (arrowKeyPressHandler) {
    document.removeEventListener('keydown', arrowKeyPressHandler);
  }
};

interface ArrowsProps {
  onChange: (value: 'up' | 'down' | 'previous' | 'next') => void;
  isFirstTask: boolean;
  isLastTask: boolean;
  isLastCriterion: boolean;
  isFirstCriterion: boolean;
  viewType?: 'cards' | 'solutions';
  isFullscreen: boolean;
  isAnyModalOpened: boolean;
  hasNextRgz?: boolean;
  hasPreviousRgz?: boolean;
  keyboardShortcutsDisabled: boolean;
  isSkipLink: boolean;
  backgroundColor?: string;
}

export const Arrows: React.FC<ArrowsProps> = (props) => {
  const classes = useStyles();

  const { isAnyDropdownOpen } = useLayoutContext();
  const { onChange, isFullscreen, isAnyModalOpened, keyboardShortcutsDisabled, isSkipLink } = props;

  const registerArrowKeyPressHandler = useCallback(() => {
    removeArrowKeyPressHandler();

    if (isFullscreen || isAnyModalOpened || isAnyDropdownOpen.current || keyboardShortcutsDisabled) {
      return;
    }

    arrowKeyPressHandler = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          {
            event.preventDefault();
            onChange(event.ctrlKey ? 'previous' : 'up');
          }
          break;
        case 'ArrowDown':
          {
            event.preventDefault();
            onChange(event.ctrlKey ? 'next' : 'down');
          }
          break;
        case 'Enter': {
          if (isSkipLink) {
            return;
          }
          event.preventDefault();
          onChange('down');
        }
      }
    };

    document.addEventListener('keydown', arrowKeyPressHandler);
  }, [isFullscreen, isAnyModalOpened, isAnyDropdownOpen, keyboardShortcutsDisabled, onChange, isSkipLink]);

  useEffect(() => {
    const rebindAfterMessageModalClose = () => registerArrowKeyPressHandler();

    registerArrowKeyPressHandler();
    window.addEventListener('focus', registerArrowKeyPressHandler);
    document.addEventListener('assessment:message-modal-closed', rebindAfterMessageModalClose);

    return () => {
      window.removeEventListener('focus', registerArrowKeyPressHandler);
      document.removeEventListener('assessment:message-modal-closed', rebindAfterMessageModalClose);
      removeArrowKeyPressHandler();
    };
  }, [registerArrowKeyPressHandler]);

  return (
    <>
      <div className={classes.container} style={{ backgroundColor: props.backgroundColor || 'transparent' }}>
        <TooltipOne
          title={!props.isFirstTask ? 'Poprzedzające zadanie w tym RGZ' : 'Poprzedzające zadanie w poprzednim RGZ'}
          placement="left"
        >
          <div
            onClick={() => props.onChange('previous')}
            className={`${classes.arrowButton} ${
              !props.isFirstTask || props.hasPreviousRgz ? classes.arrowButtonActive : ''
            }`}
          >
            <img src={DoubleArrowUp} alt="ikona poprzednie zadanie" />
          </div>
        </TooltipOne>
        <TooltipOne title={!props.isFirstCriterion ? 'Poprzednie kryterium' : ''} placement="left">
          <div
            onClick={() => {
              props.onChange('up');
            }}
            className={[classes.arrowButton]
              .concat(!props.isFirstCriterion ? [classes.arrowButtonActive] : [classes.arrowButtonDisabled])
              .join(' ')}
          >
            <img src={ArrowUp} alt="ikona poprzednie kryterium" />
          </div>
        </TooltipOne>
        <TooltipOne title={!props.isLastCriterion ? 'Następne kryterium' : ''} placement="left">
          <div
            onClick={() => {
              props.onChange('down');
            }}
            className={[classes.arrowButton]
              .concat(!props.isLastCriterion ? [classes.arrowButtonActive] : [classes.arrowButtonDisabled])
              .join(' ')}
          >
            <img src={ArrowDown} alt="ikona następne kryterium" />
          </div>
        </TooltipOne>
        <TooltipOne
          title={!props.isLastTask ? 'Kolejne zadanie w tym RGZ' : 'Kolejne zadanie w następnym RGZ'}
          placement="left"
        >
          <div
            onClick={() => props.onChange('next')}
            className={`${classes.arrowButton} ${
              !props.isLastTask || props.hasNextRgz ? classes.arrowButtonActive : ''
            }`}
          >
            <img src={DoubleArrowDown} alt="ikona następne zadanie" />
          </div>
        </TooltipOne>
      </div>
    </>
  );
};
